import { useDispatch } from 'react-redux';
import { UseFormWatch } from 'react-hook-form';
import { useState } from 'react';
import { Container, Link, Stack } from '@optimus-web/core';
import { PageFooter } from '@mews/b2b-ui';
import {
    createZodFormResolver,
    lt,
    showSuccessAlert,
    useDateTimeLocalizationInfo,
    useDialog,
    useFeatureFlag,
    useFormControls,
} from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import { RouteName, FeatureFlag, TextKey } from '@mews-commander/enums';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { ItemsAndConditionsSchemaFormValues, RoutingRuleFormValues, useCreateRoutingRuleSchema } from '../../schemas';
import { useRoutingRuleFormStepContext } from '../../contexts';
import { stepOrder, defaultValues } from '../../constants';
import { RoutingRulesFormStepName } from '../../types';
import { useAddRoutingRulesMutation } from '../../mutations/useAddRoutingRulesMutation';
import { navigateInModal } from '../../../app/actions/common';
import { RoutingRuleConflictsContextProvider } from '../../contexts/RoutingRuleConflictsContext';
import { useUpdateRoutingRulesMutation } from '../../mutations/useUpdateRoutingRulesMutation';
import { prepareRoutingRulesUpdateValues } from '../../utils/formValues';
import { useRoutingRulesData } from '../../queries';
import { RoutingRuleIdContextProvider } from '../../contexts/RoutingRuleIdContext';
import { RoutingRuleFormContent } from './RoutingRuleFormContent';
import { RoutingRuleFormFooter } from './RoutingRuleFormFooter';
import { getConflicts } from './steps/itemsAndConditions/conflicts/getConflicts';
import { RoutingRuleFormLeaveDialog } from './RoutingRuleFormLeaveDialog';
import { useInitialValues } from './hooks';

export const RoutingRuleForm = ({ routingRuleId }: { routingRuleId?: string }) => {
    const { data: routingRuleSingleResult } = useRoutingRulesData(routingRuleId);
    const { currentStep, setCurrentStep, completedSteps, setCompletedSteps } = useRoutingRuleFormStepContext();
    const resolveRoute = useResolveRoute();
    const dispatch = useDispatch();
    const [{ onClose: handleConflictsDialogClose, active: isConflictsDialogActive }, openConflictsDialog] = useDialog();
    const [conflicts, setConflicts] = useState<ReturnType<typeof getConflicts> | null>(null);
    const routingRuleSchema = useCreateRoutingRuleSchema(routingRuleId);
    const recurringRoutingRulesEnabled = useFeatureFlag(FeatureFlag.recurringRoutingRulesEnabled);
    const enterpriseId = useCurrentEnterpriseId();
    const localizationInfo = useDateTimeLocalizationInfo();

    const navigateToRoutingRulesTable = () => {
        const url = resolveRoute(RouteName.RoutingRuleIndex);
        dispatch(navigateInModal(url));
    };

    const onSuccess = (ruleId?: string) => {
        navigateToRoutingRulesTable();
        showSuccessAlert({
            title:
                routingRuleId != null
                    ? lt(TextKey.RoutingRulesFormUpdateSuccessMessage)
                    : lt(TextKey.RoutingRulesFormCreationSuccessMessage),
            description: (
                <Link
                    variant="basic"
                    href={resolveRoute(RouteName.RoutingRuleDetail, {
                        routingRuleId: ruleId,
                        query: { EnterpriseId: enterpriseId },
                    })}
                >
                    {lt(TextKey.RoutingRulesFormRequestSuccessDescription)}
                </Link>
            ),
        });
    };

    const initialValues = useInitialValues(routingRuleSingleResult, routingRuleId);
    const { mutate: addMutation, isPending: isPendingAdd } = useAddRoutingRulesMutation({ onSuccess });
    const { mutate: updateMutation, isPending: isPendingUpdate } = useUpdateRoutingRulesMutation({
        onSuccess,
        routingRuleId,
    });

    const { data } = useRoutingRulesData();

    const [leaveDialogProps, openLeaveDialog] = useDialog();

    const handleSubmit = (formValues: RoutingRuleFormValues, watch: UseFormWatch<RoutingRuleFormValues>) => {
        const currentStepIndex = stepOrder.indexOf(currentStep);
        const nextStep = stepOrder[currentStepIndex + 1];
        if (stepOrder[currentStepIndex] === RoutingRulesFormStepName.ItemsAndConditions) {
            const selectedValues: ItemsAndConditionsSchemaFormValues = watch(
                RoutingRulesFormStepName.ItemsAndConditions
            );
            const conflictsData = getConflicts(selectedValues, data, routingRuleId);
            setConflicts(conflictsData);

            if (conflictsData != null) {
                openConflictsDialog();
                return;
            }
        }
        if (currentStepIndex < stepOrder.length - 1) {
            setCompletedSteps([...completedSteps, currentStep]);
            setCurrentStep(nextStep);
        }
        if (stepOrder[currentStepIndex] === RoutingRulesFormStepName.Summary) {
            if (routingRuleId != null) {
                const transformedParams = prepareRoutingRulesUpdateValues({
                    values: formValues,
                    recurringRoutingRulesEnabled,
                    routingRuleId,
                    initialValues,
                    routingRuleResult: routingRuleSingleResult,
                    localizationInfo,
                });
                const areFormValuesUpdated =
                    Object.entries(transformedParams.routingRuleUpdates[0]).filter(
                        ([key, value]) => value !== undefined && key !== 'routingRuleId'
                    ).length > 0;
                if (areFormValuesUpdated) {
                    updateMutation(transformedParams);
                } else {
                    navigateToRoutingRulesTable();
                }
            } else {
                addMutation(formValues);
            }
        }
    };

    const { renderFormContent, submit, validate, dirtyFields, watch } = useFormControls<RoutingRuleFormValues>({
        defaultValues,
        onSubmit: (formValues) => {
            handleSubmit(formValues, watch);
        },
        resolver: createZodFormResolver(
            routingRuleSchema.pick({
                [currentStep]: true,
            })
        ),
        values: initialValues,
    });

    const onStepChange = async (step: RoutingRulesFormStepName) => {
        const isCurrentStepDirty = Object.keys(dirtyFields).some((key) => key.includes(currentStep));
        const isValid = !isCurrentStepDirty || (await validate());

        if (isValid) {
            setCurrentStep(step);
        }
    };

    const handleCancelForm = () => {
        const isFormDirty = Object.keys(dirtyFields).length > 0;
        if (isFormDirty) {
            openLeaveDialog();
        } else {
            navigateToRoutingRulesTable();
        }
    };

    return (
        <RoutingRuleIdContextProvider routingRuleId={routingRuleId}>
            <RoutingRuleConflictsContextProvider
                handleClose={handleConflictsDialogClose}
                isActive={isConflictsDialogActive}
                conflicts={conflicts}
                setConflicts={setConflicts}
                routingRuleId={routingRuleId}
            >
                <Container padding={['0', '0', '1200', '0']}>
                    <Stack distribution="space-between" vertical spacing="300">
                        {renderFormContent(<RoutingRuleFormContent onStepChange={onStepChange} />)}

                        <PageFooter>
                            <RoutingRuleFormFooter
                                submit={submit}
                                onStepChange={onStepChange}
                                onCancel={handleCancelForm}
                                isEditing={routingRuleId != null}
                                isPending={isPendingAdd || isPendingUpdate}
                            />
                        </PageFooter>
                    </Stack>
                </Container>
                <RoutingRuleFormLeaveDialog
                    {...leaveDialogProps}
                    navigateToRoutingRulesTable={navigateToRoutingRulesTable}
                />
            </RoutingRuleConflictsContextProvider>
        </RoutingRuleIdContextProvider>
    );
};
